import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Lightbulb, X, Clock, ArrowRight, Download } from 'lucide-react'
import { Button } from '@/components/ui'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useAuth } from '@/hooks/useAuth'
import { track } from '@/lib/analytics'
import { FEATURE_ROUTES } from './featureRoutes'
import {
  useAssistantSuggestion,
  useAssistantAction,
  alreadyShownToday,
  markShownToday,
  type AssistantSuggestion,
  type AssistantWalkthrough,
} from '@/hooks/useAssistantSuggestion'

// EP-73 — the floating coach bubble. Dual-gated (flag + setting); fetches at
// most once/day (localStorage). Collapsed pill → card with the walkthrough,
// time estimate, a deep-link CTA, optional generated assets, and the
// dismiss/snooze/done actions. z-[90]: above QuickActionBar (60), below
// CommandPalette (100) and toasts (100).
//
// Mobile: the QuickActionBar (`lg:hidden`, ~4rem + safe-area, full width) owns
// the bottom edge, so below `lg` the bubble sits ABOVE it
// (`bottom-[calc(env(safe-area-inset-bottom)+5rem)]`) and resets to `bottom-4`
// on desktop where the bar is gone. The expanded card also caps its height and
// scrolls so a long walkthrough never overflows a short viewport.
const ANCHOR = 'fixed right-4 z-[90] bottom-[calc(env(safe-area-inset-bottom)+5rem)] lg:bottom-4'
export function AssistantBubble() {
  const { t } = useTranslation('assistant')
  const navigate = useNavigate()
  const { user } = useAuth()
  const featureFlags = useFeatureFlags()
  const { data: accountSettings } = useAccountSettings()
  const action = useAssistantAction()
  const [expanded, setExpanded] = useState(false)

  const accountId = user?.account?.id
  const gatesOpen = Boolean(featureFlags.aiCoach && accountSettings?.settings?.ai_coach_enabled === true && accountId)

  // The once-per-day localStorage gate controls FETCHING, captured at mount so
  // marking "shown" mid-session never flips it and yanks an open bubble
  // (TanStack keeps the loaded row even after the query disables). useState's
  // lazy initializer captures it once without reading a ref during render.
  const [shownAtMount] = useState<boolean>(() => (accountId ? alreadyShownToday(accountId) : true))
  const shouldFetch = gatesOpen && !shownAtMount

  const { data: suggestion } = useAssistantSuggestion(shouldFetch)

  useEffect(() => {
    if (suggestion && accountId) markShownToday(accountId)
  }, [suggestion, accountId])

  if (!gatesOpen || !suggestion) return null

  const w = suggestion.walkthrough
  const title = displayTitle(w, suggestion, t)
  const steps = displaySteps(w, suggestion, t)
  const why = displayWhy(w, t)
  const minutes = w.time_estimate_minutes ?? 0

  const handleCta = () => {
    track('coach_clicked', { feature_id: suggestion.feature_id, kind: suggestion.kind })
    const route = routeFor(suggestion)
    if (route) navigate(route)
  }

  const act = (a: 'dismiss' | 'snooze' | 'complete') => {
    action.mutate({ id: suggestion.id, action: a })
    setExpanded(false)
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label={t('bubble.heading')}
        data-testid="coach-pill"
        className={`${ANCHOR} flex items-center gap-2 rounded-full bg-primary text-primary-content shadow-lg px-4 py-3 hover:brightness-110`}
      >
        <Lightbulb className="w-4 h-4" />
        <span className="text-sm font-medium">{t('bubble.pill')}</span>
      </button>
    )
  }

  return (
    <div data-testid="coach-card" className={`${ANCHOR} w-[min(22rem,calc(100vw-2rem))] max-h-[calc(100dvh-12rem)] overflow-y-auto lg:max-h-none lg:overflow-visible rounded-2xl bg-base-100 shadow-2xl border border-base-300`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 text-primary">
            <Lightbulb className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">{t('bubble.heading')}</span>
          </div>
          {/* The header ✕ is the gentle "not now" — it SNOOZES (hides until
              tomorrow), matching the close-affordance expectation. Permanent
              dismiss is the explicit ปิด in the action row. (Was a collapse
              no-op mislabeled "dismiss" — confused users; inspection 2026-06-13.) */}
          <Button
            variant="ghost"
            size="sm"
            shape="circle"
            onClick={() => act('snooze')}
            aria-label={t('bubble.snooze')}
            data-testid="coach-snooze"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <h3 className="mt-2 font-semibold text-base-content">{title}</h3>

        {why && (
          <p className="mt-1 text-sm text-base-content/60">{why}</p>
        )}

        {steps.length > 0 && (
          <ol className="mt-3 space-y-1.5 text-sm text-base-content/80 list-decimal list-inside">
            {steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        )}

        {minutes > 0 && (
          <div className="mt-3 flex items-center gap-1 text-xs text-base-content/50">
            <Clock className="w-3 h-3" />
            {t('bubble.time_estimate', { minutes })}
          </div>
        )}

        {w.assets && w.assets.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-medium text-base-content/60 mb-1">{t('bubble.assets_label')}</div>
            <div className="flex flex-wrap gap-2">
              {w.assets.map((asset) => (
                <Button
                  key={asset.key}
                  as="a"
                  href={`/api/images/${asset.key}`}
                  download
                  variant="outline"
                  size="xs"
                  leftIcon={<Download className="w-3 h-3" />}
                >
                  {asset.kind}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button size="sm" onClick={handleCta} data-testid="coach-cta" rightIcon={<ArrowRight className="w-4 h-4" />}>
            {suggestion.kind === 'resume' ? t('bubble.resume_cta') : t('bubble.discover_cta')}
          </Button>
          {/* Snooze lives on the header ✕; the row keeps the two decisive
              choices: done (finished it) and dismiss (don't show again). */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="xs" onClick={() => act('complete')}>
              {t('bubble.done')}
            </Button>
            <Button variant="ghost" size="xs" onClick={() => act('dismiss')} data-testid="coach-dismiss">
              {t('bubble.dismiss')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── display helpers (LLM text vs static composed) ──────────────────────────

type TFn = ReturnType<typeof useTranslation>['t']

function featureName(suggestion: AssistantSuggestion, t: TFn): string {
  const id = suggestion.walkthrough.feature_id ?? suggestion.feature_id
  return t(`features.${id}`)
}

function displayTitle(w: AssistantWalkthrough, suggestion: AssistantSuggestion, t: TFn): string {
  if (w.source === 'llm' && w.title) return w.title
  return t('static.title', { feature: featureName(suggestion, t) })
}

function displayWhy(w: AssistantWalkthrough, t: TFn): string {
  if (w.source === 'llm') return w.why_this_matters ?? ''
  return t('static.why')
}

function displaySteps(w: AssistantWalkthrough, suggestion: AssistantSuggestion, t: TFn): string[] {
  if (w.source === 'llm' && w.steps && w.steps.length > 0) return w.steps
  return [t('static.step', { feature: featureName(suggestion, t) })]
}

function routeFor(suggestion: AssistantSuggestion): string | null {
  return FEATURE_ROUTES[suggestion.feature_id] ?? null
}
